import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  effect,
  inject,
  input,
  model,
  resource,
  signal,
  viewChild,
} from '@angular/core';
import {
  ClrModalModule,
  ClrSelectModule,
  ClrFormsModule,
  ClrDatagridModule,
  ClrSpinnerModule,
  ClrTooltipModule,
} from '@clr/angular';
import { toSignal } from '@angular/core/rxjs-interop';
import { Image as KonvaImage, ImageConfig } from 'konva/lib/shapes/Image';
import { Stage, StageConfig } from 'konva/lib/Stage';
import { Layer } from 'konva/lib/Layer';
import { Circle, CircleConfig } from 'konva/lib/shapes/Circle';
import { Line, LineConfig } from 'konva/lib/shapes/Line';
import { KonvaEventObject, Node, NodeConfig } from 'konva/lib/Node';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { DatalistInputComponent } from '../../../_shared/datalist-input/datalist-input.component';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { FileForPreview } from '../../../_shared/preview-thumbnails/preview-thumbnail.type';
import { ZaehlerApiService } from 'src/app/services/APIService/zaehler.api.service';
import { HideIDColumnsPipe } from 'src/app/pipes/hide-idcolumns.pipe';
import { TablePrettyPrintPipe } from 'src/app/pipes/tablePrettyPrint.pipe';
import { ToastService } from 'src/app/services/toast.service';
import { ConfirmService } from 'src/app/services/confirm.service';
import { FormGroupOfType } from 'src/app/types/shared';
import { errorHandle } from 'src/app/misc/functions';
import { ifNotError } from 'src/app/misc/compositions';
import { TABLE_EMPTY } from 'src/app/models/ui/table/table';

@Component({
  selector: 'app-edit-skizzen-points',
  templateUrl: './edit-skizzen-points.html',
  styleUrl: './edit-skizzen-points.scss',
  imports: [
    ClrModalModule,
    MrTranslatePipe,
    HideIDColumnsPipe,
    TablePrettyPrintPipe,
    DatalistInputComponent,
    ClrSelectModule,
    ClrFormsModule,
    ClrDatagridModule,
    ClrSpinnerModule,
    ClrTooltipModule,
    ReactiveFormsModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditSkizzenPoints {
  private readonly api = inject(ZaehlerApiService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly toastr = inject(ToastService);
  private readonly confirm = inject(ConfirmService);
  private readonly destroyRef = inject(DestroyRef);

  readonly isModalOpen = model.required<boolean>();
  readonly skizze = input.required<FileForPreview>();
  readonly zWerteCodes = input<string[]>();

  private readonly konvaContainer = viewChild.required<ElementRef<HTMLDivElement>>('konvaContainer');
  private readonly stageNode = signal<Stage>(null);
  private readonly imageNode = signal<KonvaImage>(null);
  private readonly lineNode = signal<Line>(null);
  private readonly startNode = signal<Circle>(null);
  private readonly endNode = signal<Circle>(null);

  protected readonly imageRs = resource({
    params: () => this.skizze()?.base64 || undefined,
    loader: ({ params: base64 }) => {
      return new Promise<HTMLImageElement>((resolve, reject) => {
        const imageObj = new Image();
        imageObj.onload = () => resolve(imageObj);
        imageObj.onerror = err => reject(err);
        imageObj.src = `data:image/png;base64,${base64}`;
      });
    },
  });
  protected readonly configStage = computed<StageConfig>(() => {
    if (!this.imageRs.hasValue()) return {};
    return {
      width: this.imageRs.value().width,
      height: this.imageRs.value().height,
    };
  });

  protected readonly configImage = computed<ImageConfig>(() => {
    if (!this.imageRs.hasValue()) return null;
    const image = this.imageRs.value();
    return {
      image,
      x: 0,
      y: 0,
      width: image.width,
      height: image.height,
    };
  });

  protected readonly configCircle = computed<[CircleConfig, CircleConfig]>(() => {
    const { X1, Y1, X2, Y2 } = this.formValue();

    return [getCircle(X1, Y1), getCircle(X2, Y2)];

    function getCircle(x: number, y: number): CircleConfig {
      if (!x || !y) return null;
      return {
        x,
        y,
        radius: 4,
        fill: 'red',
        stroke: 'white',
        strokeWidth: 1,
        draggable: true,
      };
    }
  });

  protected readonly configLine = computed<LineConfig>(() => {
    const { X1, Y1, X2, Y2 } = this.formValue();
    if (!X1 || !Y1 || !X2 || !Y2) return null;

    return {
      points: [X1, Y1, X2, Y2],
      x: 0,
      y: 0,
      stroke: '#09e9fd94',
      strokeWidth: 25,
      lineCap: 'round',
      lineJoin: 'round',
      tension: 0,
      draggable: true,
    };
  });

  private readonly tableRs = resource({
    params: () => this.skizze()?.name || undefined,
    loader: ({ params: name }) => this.api.getSkizzenPoints(name),
  });

  protected readonly isLoading = this.tableRs.isLoading;
  protected readonly table = ifNotError(this.tableRs, TABLE_EMPTY());
  protected readonly selected = signal<SkizzenPoint[]>([]);
  protected readonly selectedID = computed(() => this.selected()[0]?.ID);

  protected readonly form = new FormGroupOfType<SkizzenPoint>({
    Code: new FormControl(null, Validators.required),
    X1: new FormControl(null, Validators.required),
    Y1: new FormControl(null, Validators.required),
    X2: new FormControl(null, Validators.required),
    Y2: new FormControl(null, Validators.required),
  });

  private readonly formValue = toSignal(this.form.valueChanges);
  protected readonly currentMode = signal<'start' | 'end' | 'none'>('none');
  protected readonly isDragging = signal(false);
  protected readonly canBeDragged = signal(false);

  constructor() {
    afterNextRender(() => this.initializeKonva());

    effect(() => {
      this.form.reset(this.selected()[0]);
      this.currentMode.set(this.selectedID() ? 'none' : 'start');
    });

    effect(() => {
      this.stageNode()?.setAttrs(this.configStage());
      this.syncNode(this.imageNode(), this.configImage());
      this.syncNode(this.lineNode(), this.configLine());

      const [startConfig, endConfig] = this.configCircle();
      this.syncNode(this.startNode(), startConfig);
      this.syncNode(this.endNode(), endConfig);
      this.stageNode()?.batchDraw();
    });

    this.destroyRef.onDestroy(() => {
      this.stageNode()?.destroy();
      this.stageNode.set(null);
      this.imageNode.set(null);
      this.lineNode.set(null);
      this.startNode.set(null);
      this.endNode.set(null);
    });
  }

  private initializeKonva() {
    const stage = new Stage({ container: this.konvaContainer().nativeElement });
    const layer = new Layer();
    const image = new KonvaImage();
    const line = new Line();
    const start = new Circle();
    const end = new Circle();

    image.on('click', event => this.onClick(event));
    line.on('click', event => this.onClick(event));
    line.on('mouseenter', () => this.canBeDragged.set(true));
    line.on('mouseleave', () => this.canBeDragged.set(false));
    line.on('dragstart', () => this.isDragging.set(true));
    line.on('dragmove', event => this.onLineMoving(event));
    line.on('dragend', event => {
      this.isDragging.set(false);
      this.onLineMoved(event);
    });

    this.addCircleListeners(start, 'start');
    this.addCircleListeners(end, 'end');

    layer.add(image, line, start, end);
    stage.add(layer);

    this.stageNode.set(stage);
    this.imageNode.set(image);
    this.lineNode.set(line);
    this.startNode.set(start);
    this.endNode.set(end);
  }

  private addCircleListeners(circle: Circle, mode: 'start' | 'end') {
    circle.on('mouseenter', () => this.canBeDragged.set(true));
    circle.on('mouseleave', () => this.canBeDragged.set(false));
    circle.on('dragstart', () => this.isDragging.set(true));
    circle.on('dragmove', event => this.onCircleMoving(event, mode));
    circle.on('dragend', event => {
      this.isDragging.set(false);
      this.onCircleMoved(event, mode);
    });
  }

  private syncNode(node: KonvaImage, config: ImageConfig): void;
  private syncNode(node: Line, config: LineConfig): void;
  private syncNode(node: Circle, config: CircleConfig): void;
  private syncNode(node: Node<NodeConfig>, config: NodeConfig) {
    node?.visible(!!config);
    if (config) node.setAttrs(config);
  }

  private onClick(event: KonvaEventObject<MouseEvent>) {
    if (this.currentMode() == 'none') return;

    let { x, y } = event.target.getRelativePointerPosition();
    x = Math.round(x);
    y = Math.round(y);

    switch (this.currentMode()) {
      case 'start': {
        this.form.patchValue({ X1: x, Y1: y });
        if (!this.selectedID()) this.currentMode.set('end');
        break;
      }
      case 'end': {
        this.form.patchValue({ X2: x, Y2: y });
        if (!this.selectedID()) this.currentMode.set('none');
        break;
      }
    }
  }

  private onCircleMoving(event: KonvaEventObject<DragEvent>, mode: 'start' | 'end') {
    const circle = event.target as Circle;
    const lineNode = this.lineNode();

    let [x1, y1, x2, y2] = lineNode.points();
    const { x, y } = circle.position();
    if (mode == 'start') {
      x1 = x;
      y1 = y;
    } else {
      x2 = x;
      y2 = y;
    }

    lineNode.points([x1, y1, x2, y2]);
    circle.getLayer()?.batchDraw();
  }

  private onCircleMoved(event: KonvaEventObject<DragEvent>, mode: 'start' | 'end') {
    const circle = event.target as Circle;
    const x = Math.round(circle.x());
    const y = Math.round(circle.y());

    switch (mode) {
      case 'start': {
        this.form.patchValue({ X1: x, Y1: y });
        break;
      }
      case 'end': {
        this.form.patchValue({ X2: x, Y2: y });
        break;
      }
    }
  }

  private onLineMoving(event: KonvaEventObject<DragEvent>) {
    const line = event.target as Line;
    const offsetX = line.x();
    const offsetY = line.y();

    const start = this.startNode();
    const end = this.endNode();

    const { X1, Y1, X2, Y2 } = this.formValue();
    start.position({ x: X1 + offsetX, y: Y1 + offsetY });
    end.position({ x: X2 + offsetX, y: Y2 + offsetY });

    line.getLayer()?.batchDraw();
  }

  private onLineMoved(event: KonvaEventObject<DragEvent>) {
    const line = event.target as Line;
    const [x1, y1, x2, y2] = line.points();

    const offsetX = Math.round(line.x());
    const offsetY = Math.round(line.y());

    line.x(0);
    line.y(0);

    this.form.patchValue({
      X1: x1 + offsetX,
      Y1: y1 + offsetY,
      X2: x2 + offsetX,
      Y2: y2 + offsetY,
    });
  }

  protected async saveIfValid() {
    if (this.form.invalid) return;

    try {
      const ID = await this.api.setSkizzenPoint(this.skizze().name, this.normalizedPoint());

      this.toastr.success(this.mrTranslate.transform('Daten gelöscht'));
      this.tableRs.reload();
      this.selected.set([{ ID } as SkizzenPoint]);
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Etwas ist schief gelaufen'));
    }
  }

  private normalizedPoint(): SkizzenPoint {
    let { Code, X1, Y1, X2, Y2 } = this.formValue();
    if (Math.abs(X1 - X2) <= 3) {
      const X = Math.round((X1 + X2) / 2);
      X1 = X2 = X;
    }

    return { ID: this.selectedID(), Code, X1, Y1, X2, Y2 };
  }

  protected async deletePoint() {
    try {
      const text = this.mrTranslate.transform('Sind Sie sicher, dass Sie diese Zeile löschen wollen?');
      const ok = await this.confirm.danger(text);
      if (!ok) return;

      await this.api.deleteSkizzenPoint(this.selectedID());
      this.toastr.success(this.mrTranslate.transform('Daten gelöscht'));
      this.tableRs.reload();
      this.selected.set([]);
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Etwas ist schief gelaufen'));
    }
  }
}

export interface SkizzenPoint {
  ID?: number;
  /** @see ZählerWert.Code */
  Code: string;
  X1: number;
  Y1: number;
  X2: number;
  Y2: number;
}
